// Locked screen — the unified access-blocked look (LOCKED-SCREEN task).
// A SYNTHETIC content kind: the server withholds a locked message's body, and
// the surface renders this document built from the access grant instead. It is
// never stored, never round-trips a save, and therefore has no rich_data
// envelope, no KNOWN_DATA_KINDS entry, and no sanitizer allowlist footprint.
// Like contacts, display is ONE strongly templated string builder (every field
// escaped) shared by the React renderer and the string display path.

export type LockedNoticeReason = 'show-pin' | 'time'

export interface LockedNoticeData {
  codeKey: string
  nameKey: string
  subject: string
  reason: LockedNoticeReason
  /** Human-readable availability line for time-locked messages (replaces the
   * generic note when present). */
  timeInfo: string
  /** Same-origin POST target for the pin form; empty renders no form. The
   * form renders for BOTH reasons: a time lock is opened by the message's
   * edit password (legacy parity — the owner override), a show lock by
   * either password; the server re-validates every entry. */
  unlockAction: string
}

export interface LockedNoticeInput {
  codeKey: string
  reason: LockedNoticeReason
  nameKey?: string
  subject?: string
  timeInfo?: string
  unlockAction?: string
}

export interface LockedNoticeDocument {
  kind: 'locked'
  value: string
}

const FIELD_MAX = 300

function coerceField(value: unknown): string {
  return typeof value === 'string' ? value.slice(0, FIELD_MAX) : ''
}

/** The pin form posts only to a same-origin path — anything else is dropped. */
function coerceAction(value: unknown): string {
  const action = coerceField(value)
  return action.startsWith('/') && !action.startsWith('//') ? action : ''
}

function coerceData(raw: unknown): LockedNoticeData | null {
  const data = (raw && typeof raw === 'object' ? raw : null) as Record<string, unknown> | null
  if (!data) return null
  const reason = data.reason
  if (reason !== 'show-pin' && reason !== 'time') return null
  const codeKey = coerceField(data.codeKey)
  if (!codeKey) return null
  return {
    codeKey,
    nameKey: coerceField(data.nameKey),
    subject: coerceField(data.subject),
    reason,
    timeInfo: coerceField(data.timeInfo),
    unlockAction: coerceAction(data.unlockAction),
  }
}

export function lockedNoticeDocument(input: LockedNoticeInput): LockedNoticeDocument {
  const data = coerceData(input)
  return { kind: 'locked', value: JSON.stringify(data ?? { codeKey: '', reason: 'show-pin' }) }
}

export function parseLockedNotice(value: string): LockedNoticeData | null {
  try {
    return coerceData(JSON.parse(value))
  } catch {
    return null
  }
}

// --- strongly templated view -----------------------------------------------
// The envelope face: the message shows its address (codekey / name / subject)
// while the content stays sealed; the gray password input is the way in.
// Fixed template slots, all values escaped; surfaces re-theme through the
// --content-locked-* CSS vars (the package-owned token pattern the content
// frames and contacts view use). Plain form POST — works server-rendered,
// before hydration, and without JS.

function escapeText(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

const ROOT_STYLE = 'display:flex;flex-direction:column;align-items:center;text-align:center;gap:6px;padding:var(--content-locked-padding, 48px 24px);color:var(--content-locked-fg, #2b3440)'
const ICON_STYLE = 'color:var(--content-locked-icon, #9aa3ad);margin-bottom:2px'
const KEY_STYLE = 'font-family:ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;font-size:var(--content-locked-key-size, 12px);letter-spacing:0.08em;color:var(--content-locked-muted, #6b7280)'
const NAME_STYLE = 'font-size:var(--content-locked-name-size, 20px);font-weight:700;line-height:1.25;color:var(--content-locked-fg, #2b3440)'
const SUBJECT_STYLE = 'font-size:var(--content-locked-subject-size, 13px);color:var(--content-locked-muted, #6b7280)'
const NOTE_STYLE = 'margin-top:10px;font-size:12px;color:var(--content-locked-faint, #9aa3ad)'
const TIME_STYLE = 'margin-top:2px;font-size:13px;font-weight:600;color:var(--content-locked-muted, #6b7280)'
const FORM_STYLE = 'margin:8px 0 0;display:flex;gap:8px;justify-content:center;flex-wrap:wrap'
const INPUT_STYLE = 'background:var(--content-locked-input-bg, #f3f4f6);border:1px solid var(--content-locked-input-border, #d7dbe0);border-radius:var(--content-locked-input-radius, 4px);padding:8px 12px;font-size:14px;text-align:center;letter-spacing:0.25em;width:180px;max-width:100%;color:inherit;outline-offset:2px'
const BUTTON_STYLE = 'background:var(--content-locked-button-bg, #e5e7eb);border:1px solid var(--content-locked-input-border, #d7dbe0);border-radius:var(--content-locked-input-radius, 4px);padding:8px 14px;font-size:13px;font-weight:600;color:var(--content-locked-fg, #2b3440);cursor:pointer'

const LOCK_ICON = '<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4.5" y="10.5" width="15" height="10" rx="2"></rect><path d="M8 10.5V7a4 4 0 0 1 8 0v3.5"></path></svg>'

export function renderLockedNoticeHtml(data: LockedNoticeData): string {
  const parts: string[] = []
  parts.push(`<div style="${ICON_STYLE}">${LOCK_ICON}</div>`)
  parts.push(`<div style="${KEY_STYLE}">@${escapeText(data.codeKey)}</div>`)
  if (data.nameKey) parts.push(`<div style="${NAME_STYLE}">${escapeText(data.nameKey)}</div>`)
  if (data.subject) parts.push(`<div style="${SUBJECT_STYLE}">${escapeText(data.subject)}</div>`)
  if (data.reason === 'time') {
    parts.push(`<div style="${TIME_STYLE}">${escapeText(data.timeInfo || 'This message is not available right now.')}</div>`)
    parts.push(`<div style="${NOTE_STYLE}">The message password still opens it.</div>`)
  } else {
    parts.push(`<div style="${NOTE_STYLE}">Enter the password to open this message.</div>`)
  }
  if (data.unlockAction) {
    parts.push(
      `<form method="post" action="${escapeText(data.unlockAction)}" style="${FORM_STYLE}">`
      + `<input type="hidden" name="codekey" value="${escapeText(data.codeKey)}" />`
      + `<input type="password" name="show_pin" placeholder="Password" aria-label="Password" autocomplete="off" style="${INPUT_STYLE}" />`
      + `<button type="submit" style="${BUTTON_STYLE}">Open</button>`
      + '</form>',
    )
  }
  return `<div style="${ROOT_STYLE}">${parts.join('')}</div>`
}
